/**
 * BED — Une ligne de règle d'automatisation (migration 00233).
 *
 * Édition INLINE et immédiate : activer/désactiver, régler le délai, le
 * plafond, l'anti-spam, le canal et le modèle d'email. Chaque champ envoie
 * uniquement sa propre valeur — le serveur `COALESCE` le reste.
 *
 * Les règles `is_builtin` (celles du cahier des charges) restent réglables
 * mais ne se suppriment pas : c'est la garde posée en base (00233 §12).
 */

import { useState } from 'react';
import { CalendarClock, Mail, MessageSquare, Zap } from 'lucide-react';
import { Badge, Input, Select, Toggle } from '../ui';
import type {
  BedAutomationRule,
  BedAutomationChannel,
  BedRulePatch,
} from '../../lib/bed/automations';
import type { EmailTemplate } from '../../types/emailTemplate';

interface BedAutomationRuleRowProps {
  rule: BedAutomationRule;
  templates: EmailTemplate[];
  /** Nombre d'équipages actuellement dus pour cette règle (relances). */
  dueNow: number;
  /** 00297 — règle désactivée : équipages qui la recevraient si on l'activait. */
  recipientsIfActivated?: number;
  disabled: boolean;
  onPatch: (key: string, patch: BedRulePatch) => void;
}

/** Le délai d'une relance « avant deadline » se lit J−X, pas J+X. */
function delayLabel(rule: BedAutomationRule): string {
  if (rule.kind === 'event') return 'immédiat';
  return rule.trigger_key === 'before_deadline'
    ? `J−${rule.delay_days} avant la date limite`
    : `J+${rule.delay_days}`;
}

export function BedAutomationRuleRow({
  rule,
  templates,
  dueNow,
  recipientsIfActivated = 0,
  disabled,
  onPatch,
}: BedAutomationRuleRowProps) {
  // Champs numériques en état local : on ne veut pas appeler le serveur à
  // chaque frappe, seulement au blur (ou à Entrée).
  const [delay, setDelay] = useState(String(rule.delay_days ?? ''));
  const [cooldown, setCooldown] = useState(String(rule.min_days_between_sends));
  const [maxSends, setMaxSends] = useState(rule.max_sends == null ? '' : String(rule.max_sends));

  const commitNumber = (raw: string, field: keyof BedRulePatch, fallback: number | null) => {
    const trimmed = raw.trim();
    // Champ vidé sur le plafond = « illimité » : le serveur attend -1, car
    // NULL signifie déjà « ne change rien ».
    if (trimmed === '') {
      if (field === 'max_sends' && fallback !== null) onPatch(rule.key, { max_sends: -1 });
      return;
    }
    const value = Number(trimmed);
    if (!Number.isInteger(value) || value < 0) return;
    if (value === fallback) return;
    onPatch(rule.key, { [field]: value } as BedRulePatch);
  };

  return (
    <div className="flex flex-col gap-3 border-b border-zinc-100 px-page-padding py-4 last:border-b-0">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            {rule.kind === 'event' ? (
              <Zap className="size-4 shrink-0 text-amber-600" aria-hidden />
            ) : (
              <CalendarClock className="size-4 shrink-0 text-teal-600" aria-hidden />
            )}
            <span className="text-sm font-semibold text-zinc-900">{rule.label}</span>
            <Badge variant="secondary">{rule.kind === 'event' ? 'Événement' : 'Relance'}</Badge>
            <span className="text-xs text-zinc-500">{delayLabel(rule)}</span>
            {rule.kind === 'reminder' && rule.is_active && dueNow > 0 && (
              <Badge variant="outline">{dueNow} équipage(s) dus</Badge>
            )}
            {rule.kind === 'reminder' && !rule.is_active && recipientsIfActivated > 0 && (
              <Badge variant="outline">
                {recipientsIfActivated} équipage(s) la recevraient si vous l’activez
              </Badge>
            )}
          </div>
          {rule.description && (
            <p className="max-w-3xl text-xs leading-5 text-zinc-500">{rule.description}</p>
          )}
          <p className="text-xs text-zinc-400">
            {rule.sent_count} envoi(s) · {rule.pending_count} en attente
            {rule.last_sent_at
              ? ` · dernier le ${new Date(rule.last_sent_at).toLocaleDateString('fr-FR')}`
              : ''}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-zinc-500">{rule.is_active ? 'Active' : 'Inactive'}</span>
          <Toggle
            checked={rule.is_active}
            disabled={disabled}
            onChange={(checked) => onPatch(rule.key, { is_active: checked })}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {rule.kind === 'reminder' && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-zinc-600">
              {rule.trigger_key === 'before_deadline' ? 'Jours avant' : 'Délai (jours)'}
            </span>
            <Input
              type="number"
              min={0}
              className="w-24"
              value={delay}
              disabled={disabled}
              onChange={(e) => setDelay(e.target.value)}
              onBlur={() => commitNumber(delay, 'delay_days', rule.delay_days)}
            />
          </label>
        )}

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-zinc-600">Anti-spam (jours)</span>
          <Input
            type="number"
            min={0}
            className="w-24"
            value={cooldown}
            disabled={disabled}
            onChange={(e) => setCooldown(e.target.value)}
            onBlur={() =>
              commitNumber(cooldown, 'min_days_between_sends', rule.min_days_between_sends)
            }
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-zinc-600">Plafond (vide = illimité)</span>
          <Input
            type="number"
            min={1}
            className="w-32"
            value={maxSends}
            disabled={disabled}
            onChange={(e) => setMaxSends(e.target.value)}
            onBlur={() => commitNumber(maxSends, 'max_sends', rule.max_sends)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-zinc-600">Canal</span>
          <Select
            className="w-32"
            value={rule.channel}
            disabled={disabled}
            onChange={(e) => onPatch(rule.key, { channel: e.target.value as BedAutomationChannel })}
            options={[
              { value: 'email', label: 'Email' },
              { value: 'sms', label: 'SMS (non branché)' },
            ]}
          />
        </label>

        <label className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-xs font-medium text-zinc-600">Modèle d'email</span>
          <Select
            value={rule.email_template_id ?? ''}
            disabled={disabled}
            onChange={(e) => onPatch(rule.key, { email_template_id: e.target.value })}
            options={templates.map((t) => ({ value: t.id, label: t.name }))}
            placeholder="— aucun —"
          />
        </label>
      </div>

      {rule.channel === 'sms' && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <MessageSquare className="size-4 shrink-0" aria-hidden />
          <span>
            Aucun fournisseur SMS n'est configuré : ces envois sont sélectionnés, rendus et tracés,
            mais marqués « non envoyé ». Brancher un fournisseur est la seule pièce manquante.
          </span>
        </div>
      )}

      {rule.channel === 'email' && !rule.email_template_id && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          <Mail className="size-4 shrink-0" aria-hidden />
          <span>Aucun modèle d'email associé : cette règle n'enverra rien.</span>
        </div>
      )}
    </div>
  );
}
